import type { SurveyQuestionData } from './types';
import type { ManagerAssessmentQuestionAnswerPayload } from '@/api/cycle-evaluation-services/manager-assessment/managerAssessmentService';

import { QuestionType } from '@/constants/QuestionType';
import {
    isOtherOptionSelected,
    type QuestionAnswerFormValues,
} from '@/pages/SurveyAnswer/SurveyAnswer/validation';
import { transformToReviewQuestionAnswer } from '@/utils/reviewQuestionAnswer';

/** The survey's local answer state, keyed by question id. */
export interface SurveyAnswerState {
    answerValues: Record<string, string[]>;
    otherTextValues: Record<string, string>;
}

const resolveYesNoSelectedOptionIds = (
    question: SurveyQuestionData,
    boolValue: boolean | undefined,
    selectedOptionIds: string[],
): string[] => {
    if (selectedOptionIds.length > 0 || boolValue === undefined) {
        return selectedOptionIds;
    }

    const orderedOptions = [...(question.reviewQuestion.reviewQuestionOptions ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
    );

    if (orderedOptions.length === 0) {
        return selectedOptionIds;
    }

    if (orderedOptions.length === 1) {
        return [orderedOptions[0].id];
    }

    return [boolValue ? orderedOptions[0].id : orderedOptions[1].id];
};

/**
 * Maps one question's local state onto the API payload. Splits the shared Answer model into per-type form
 * fields, then reuses the same transform the survey-answer page uses so each type lands in the right column
 * (rating -> intValue, yes/no -> boolValue, emoji/ranking -> resolved option ids).
 *
 * An unanswered question yields a row of nulls flagged as skipped rather than nothing — the draft endpoint
 * merges what it is given, so clearing an answer has to be sent explicitly to overwrite what was autosaved
 * earlier, and the flag is what keeps that row out of the counts and averages computed from the answers.
 */
export const buildQuestionAnswerPayload = (
    question: SurveyQuestionData,
    { answerValues, otherTextValues }: SurveyAnswerState,
): ManagerAssessmentQuestionAnswerPayload => {
    const values = answerValues[question.competencyId] ?? [];
    const hasBeenSkipped = values.length === 0;

    const formValues: QuestionAnswerFormValues = {
        questionId: question.competencyId,
        questionType: question.questionType,
        selectedValues: [],
    };
    switch (question.questionType) {
        case QuestionType.PLAIN_TEXT:
            formValues.textValue = values[0] ?? '';
            break;
        case QuestionType.RATING_SCALE:
            formValues.intValue = values[0] ? parseInt(values[0], 10) : undefined;
            break;
        case QuestionType.YES_NO:
            formValues.selectedValues = values;
            formValues.boolValue =
                values[0] === 'yes' ? true : values[0] === 'no' ? false : undefined;
            break;
        default:
            formValues.selectedValues = values;
            // Include "Other" text when the Other option is selected
            if (
                question.reviewQuestion.hasOtherOption &&
                isOtherOptionSelected(values, question.reviewQuestion)
            ) {
                formValues.textValue = otherTextValues[question.competencyId] ?? '';
            }
            break;
    }

    const reviewQuestionAnswer = transformToReviewQuestionAnswer({
        formValues,
        reviewQuestion: question.reviewQuestion,
        reviewAnswerId: '',
        hasBeenSkipped,
    });
    const selectedOptionIds = resolveYesNoSelectedOptionIds(
        question,
        formValues.boolValue,
        reviewQuestionAnswer.selectedOptions?.map((option) => option.reviewQuestionOptionId) ?? [],
    );

    return {
        reviewQuestionId: question.competencyId,
        hasBeenSkipped,
        textValue: reviewQuestionAnswer.textValue ?? null,
        intValue: reviewQuestionAnswer.intValue ?? null,
        boolValue: reviewQuestionAnswer.boolValue ?? null,
        selectedOptionIds,
    };
};

/**
 * Submit treats the set of answers it receives as authoritative and drops the rest, so every answerable
 * question needs a row: an instance carrying no stored answer cannot be told apart from one that was
 * never submitted.
 */
export const buildSubmissionAnswersPayload = (
    questions: SurveyQuestionData[],
    state: SurveyAnswerState,
): ManagerAssessmentQuestionAnswerPayload[] =>
    questions
        .filter((question) => question.questionType !== QuestionType.SECTION_TITLE)
        .map((question) => buildQuestionAnswerPayload(question, state));
